import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../../Api';
import { useNotification } from '../../Components/GlobalNotification';
import { AuthShell, CheckIcon, PasswordField } from './AuthShell';

// Public signup is Host-only for now (role 2). The traveler tab is built but hidden;
// set to true once guest signup is approved (the backend /register must also accept role 3).
const TRAVELER_SIGNUP_ENABLED = false;
const HOST_ROLE = '2';
const GUEST_ROLE = '3';

const SuccessIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M7.500 12.500l3 3 6-6.500" /></svg>
);

export const VacationsRegister = () => {
    const { showNotification } = useNotification();
    const { ApiData: RegisterResponse, loading, error: RegisterError, post: submitData } = useAuth('/register');

    const [tab, setTab] = useState('host');
    const [done, setDone] = useState(false);
    const [submittedEmail, setSubmittedEmail] = useState('');
    const [host, setHost] = useState({ name: '', email: '', password: '', terms: false, marketing: false });
    const [guest, setGuest] = useState({ first: '', last: '', email: '', password: '', marketing: false });

    useEffect(() => {
        document.title = 'Kayifi Vacations — Register';
    }, []);

    useEffect(() => {
        if (RegisterResponse) setDone(true);
    }, [RegisterResponse]);

    // The API returns validation errors as { message: 'Validation Error.', data: { email: ['...'] } }
    const fieldErrors = RegisterError?.data && typeof RegisterError.data === 'object' && !Array.isArray(RegisterError.data)
        ? Object.fromEntries(Object.entries(RegisterError.data).map(([field, msgs]) => [field, Array.isArray(msgs) ? msgs[0] : String(msgs)]))
        : {};

    useEffect(() => {
        if (RegisterError) {
            const details = Object.values(fieldErrors);
            const msg = details.length
                ? details.join(' ')
                : (RegisterError?.message || 'Registration failed. Please try again.');
            showNotification({ type: 'error', title: 'Error', message: msg });
        }
    }, [RegisterError]);

    const handleHostSubmit = (e) => {
        e.preventDefault();
        if (!e.currentTarget.checkValidity()) {
            e.currentTarget.reportValidity();
            return;
        }
        setSubmittedEmail(host.email);
        submitData({ name: host.name, email: host.email, password: host.password, password_confirmation: host.password, role: HOST_ROLE });
    };

    const handleGuestSubmit = (e) => {
        e.preventDefault();
        if (!e.currentTarget.checkValidity()) {
            e.currentTarget.reportValidity();
            return;
        }
        setSubmittedEmail(guest.email);
        submitData({ name: `${guest.first} ${guest.last}`.trim(), email: guest.email, password: guest.password, password_confirmation: guest.password, role: GUEST_ROLE });
    };

    const hostOnly = !TRAVELER_SIGNUP_ENABLED;

    return (
        <AuthShell>
            
                <section className="reg-hero">
                    <div className="container reg-wrap">
                        <div className="reg-pitch">
                            <span className="eyebrow">vacations.kayifi.com</span>
                            {hostOnly ? (
                                <>
                                    <h1>List your property. <span className="accent">Join Kayifi Vacations.</span></h1>
                                    <p>Create a free host account to list your villa, condo or getaway and welcome guests from across the Caribbean.</p>
                                    <ul className="perks">
                                        <li><CheckIcon /><span>Add one or more listings and manage them from your own dashboard</span></li>
                                        <li><CheckIcon /><span>Edit, update or remove your listings any time</span></li>
                                        <li><CheckIcon /><span>Reach travelers across the Caribbean</span></li>
                                    </ul>
                                </>
                            ) : (
                                <>
                                    <h1>Stay longer. <span className="accent">Join Kayifi Vacations.</span></h1>
                                    <p>Create a free account to book hand-picked villas, condos and getaways across the Caribbean, or list your own property and welcome guests.</p>
                                    <ul className="perks">
                                        <li><CheckIcon /><span>Save favorite stays and plan trips in one place</span></li>
                                        <li><CheckIcon /><span>Book directly with local hosts and managers</span></li>
                                        <li><CheckIcon /><span>Owners and managers: list your property and reach travelers across the Caribbean</span></li>
                                    </ul>
                                </>
                            )}
                        </div>

                        <div className="reg-card">
                            {TRAVELER_SIGNUP_ENABLED && !done && (
                                <div className="tabs" role="tablist" aria-label="Account type">
                                    <button className="tab" role="tab" type="button" aria-selected={tab === 'guest'} onClick={() => setTab('guest')}>I'm a traveler</button>
                                    <button className="tab" role="tab" type="button" aria-selected={tab === 'host'} onClick={() => setTab('host')}>I'm an owner / manager</button>
                                </div>
                            )}

                            {done ? (
                                <div className="success">
                                    <SuccessIcon />
                                    <h2>Check your email</h2>
                                    <p>We sent a verification link to <b>{submittedEmail}</b>. Verify your email, then sign in to start adding your property.</p>
                                    <p style={{ marginTop: '1rem' }}><Link className="btn btn-primary btn-lg" to="/login">Go To Sign In</Link></p>
                                </div>
                            ) : (
                                <>
                                    {tab === 'host' && (
                                        <form onSubmit={handleHostSubmit} noValidate>
                                            <h2>List Your Property</h2>
                                            <p className="lead">Create your host account first. You'll add property details, photos and rates next.</p>
                                            <div className="field">
                                                <label htmlFor="h-name">Full name</label>
                                                <input id="h-name" name="full_name" autoComplete="name" placeholder=" " required value={host.name} onChange={(e) => setHost({ ...host, name: e.target.value })} />
                                                {fieldErrors.name && <div className="field-error">{fieldErrors.name}</div>}
                                            </div>
                                            <div className="field">
                                                <label htmlFor="h-email">Email</label>
                                                <input id="h-email" name="email" type="email" autoComplete="email" placeholder="you@example.com" required value={host.email} onChange={(e) => setHost({ ...host, email: e.target.value })} />
                                                {fieldErrors.email && <div className="field-error">{fieldErrors.email}</div>}
                                            </div>
                                            <PasswordField id="h-pw" value={host.password} error={fieldErrors.password} onChange={(e) => setHost({ ...host, password: e.target.value })} />
                                            <div className="step-note">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
                                                <span>Next steps after you sign up: property type and address, rooms, photos, rates and payout details.</span>
                                            </div>
                                            <label className="check">
                                                <input type="checkbox" name="terms" required checked={host.terms} onChange={(e) => setHost({ ...host, terms: e.target.checked })} />
                                                <span>I agree to the Kayifi <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.</span>
                                            </label>
                                            <button className="btn btn-primary btn-lg" type="submit" disabled={loading}>{loading ? 'Creating Account…' : 'Create Host Account'}</button>
                                        </form>
                                    )}

                                    {TRAVELER_SIGNUP_ENABLED && tab === 'guest' && (
                                        <form onSubmit={handleGuestSubmit} noValidate>
                                            <h2>Create your account</h2>
                                            <p className="lead">It takes less than a minute. No payment needed.</p>
                                            <div className="row">
                                                <div className="field"><label htmlFor="g-first">First name</label><input id="g-first" autoComplete="given-name" placeholder=" " required value={guest.first} onChange={(e) => setGuest({ ...guest, first: e.target.value })} /></div>
                                                <div className="field"><label htmlFor="g-last">Last name</label><input id="g-last" autoComplete="family-name" placeholder=" " required value={guest.last} onChange={(e) => setGuest({ ...guest, last: e.target.value })} /></div>
                                            </div>
                                            <div className="field"><label htmlFor="g-email">Email</label><input id="g-email" type="email" autoComplete="email" placeholder="you@example.com" required value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} /></div>
                                            <PasswordField id="g-pw" value={guest.password} onChange={(e) => setGuest({ ...guest, password: e.target.value })} />
                                            <button className="btn btn-primary btn-lg" type="submit" disabled={loading}>{loading ? 'Creating Account…' : 'Create Account'}</button>
                                        </form>
                                    )}

                                    <p className="switch">Already have an account? <Link to="/login">Sign In</Link></p>
                                </>
                            )}
                        </div>
                    </div>
                </section>
        </AuthShell>
    );
};
